import {
  Badge,
  Flex,
  Image,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { FilterBar, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { Tooltip } from '../../../components/Tooltip'
import { useHasRole, useAppToast } from '../../../helpers'
import { ISector, SectorService } from '../../../services/Sector/SectorService'
import { IUser, UserService } from '../../../services/User/UserService'
import { IUserGroup, UserGroupService } from '../../../services/UserGroup/UserGroupService'
import { IListFilters } from './models'
import { UserCreateModal } from './UserCreateModal'
import { UserEditModal } from './UserEditModal'
import { UserSetRolesModal } from './UserSetRolesModal'

const INITIAL_FILTER: IListFilters = {
  status: 'active',
  group: '',
  search: '',
  sector: ''
}

const STATUS_OPTIONS = [
  { value: 'active', label: 'Ativo' },
  { value: 'pending_approval', label: 'Aprovação Pendente' },
  { value: 'restricted', label: 'Acesso Restrito' }
]

export default function UsersPage() {
  const userService = new UserService()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [totalPages, setTotalPages] = useState(1)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [users, setUsers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isUserCreateModalOpen, setIsUserCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [modalUser, setModalUser] = useState<any>({ roles: [] })
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isOpenDeleteDialog, onOpen: onOpenDeleteDialog, onClose: onCloseDeleteDialog } = useDisclosure()
  const [restrict, setRestrict] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const userGroupService = new UserGroupService()
  const sectorService = new SectorService()
  const [userGroups, setUserGroups] = useState<IUserGroup[]>()
  const [filter, setFilter] = useState<IListFilters>(INITIAL_FILTER)
  const hasRole = useHasRole()
  const toast = useAppToast()
  const permissions = {
    addUser: hasRole('add_user'),
    editUser: hasRole('edit_user'),
    restrictUser: hasRole('restrict_user'),
    deleteUser: hasRole('delete_user')
  }
  const [sectors, setSectors] = useState<ISector[]>()
  // A listagem já abre filtrada por "Ativo": o que conta como sujo é divergir do estado inicial,
  // não ter algum campo preenchido.
  const isDirty = (Object.keys(INITIAL_FILTER) as (keyof IListFilters)[]).some(
    (key) => filter[key] !== INITIAL_FILTER[key]
  )

  const handleCloseModal = () => {
    setIsModalOpen(false)
    loadUsers(page)
  }

  const loadUsers = async (page: number) => {
    setLoading(true)
    setLoadError(false)
    try {
      const { total_selected, per_page, data } = await userService.list({ page, pageSize, filter })

      setUsers(data)

      setTotalPages(Math.ceil(total_selected / per_page))
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar os usuários', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchUserGroups = async () => {
    await userGroupService.listForSelect().then((data) => {
      setUserGroups(data)
    })
  }

  const fetchSectors = async () => {
    await sectorService.listForSelect().then((data) => {
      setSectors(data)
    })
  }

  useEffect(() => {
    fetchUserGroups()
    fetchSectors()
  }, [])

  // O debounce vive dentro do `FilterSearch`: aqui o filtro já chega pronto, e trocar
  // um select responde na hora em vez de esperar meio segundo.
  useEffect(() => {
    loadUsers(page)
  }, [filter, page, pageSize])

  // Trocar um filtro volta para a página 1 — filtrar na página 5 pediria a página 5 do
  // resultado novo, quase sempre vazia. Os dois `setState` são agrupados numa renderização
  // só, então o efeito acima busca uma vez.
  const updateFilter = (patch: Partial<IListFilters>) => {
    setFilter({ ...filter, ...patch })
    setPage(1)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOpenModal = (user: any) => {
    setModalUser(user)
    setIsModalOpen(true)
  }

  const handleCloseUserCreateModal = () => {
    setIsUserCreateModalOpen(false)
    loadUsers(page)
  }

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false)
    loadUsers(page)
  }

  const handleDelete = async () => {
    setIsSubmitting(true)
    try {
      await userService.destroy(modalUser.id)
      toast.success('Usuário excluído')
      onCloseDeleteDialog()
      loadUsers(page)
    } catch (error) {
      toast.error('Erro ao excluir usuário', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggleRestriction = async () => {
    setIsSubmitting(true)
    try {
      await (restrict ? userService.restrict(modalUser.id) : userService.unrestrict(modalUser.id))
      toast.success(restrict ? 'Acesso restringido' : 'Acesso liberado')
      onClose()
      loadUsers(page)
    } catch (error) {
      toast.error(restrict ? 'Erro ao restringir o acesso' : 'Erro ao liberar o acesso', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  function renderStatus(user: IUser) {
    if (user.restricted) {
      return <Badge colorScheme="red">Acesso Restrito</Badge>
    } else if (user.pending_approval) {
      return <Badge colorScheme="yellow">Aprovação Pendente</Badge>
    } else {
      return <Badge colorScheme="green">Ativo</Badge>
    }
  }

  return (
    <>
      <PageTitle title="Usuários" description="Contas com acesso ao painel e seus grupos" />
      <FilterBar onClear={() => updateFilter(INITIAL_FILTER)} isDirty={isDirty}>
        <FilterSelect
          label="Status"
          value={filter.status}
          onChange={(status) => updateFilter({ status: status as IListFilters['status'] })}
          options={STATUS_OPTIONS}
        />
        <FilterSelect
          label="Grupo"
          value={filter.group}
          onChange={(group) => updateFilter({ group })}
          options={(userGroups ?? []).map((userGroup) => ({ value: userGroup.id, label: userGroup.name }))}
        />
        <FilterSelect
          label="Setor"
          value={filter.sector}
          onChange={(sector) => updateFilter({ sector })}
          options={(sectors ?? []).map((sector) => ({ value: sector.id, label: sector.name }))}
        />
        <FilterSearch
          value={filter.search}
          onChange={(search) => updateFilter({ search })}
          placeholder="nome / e-mail"
        />
      </FilterBar>
      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>Nome</Th>
              <Th>Status</Th>
              <Th>E-mail</Th>
              <Th>Grupo</Th>
              <Th>Setores</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={6} />
            ) : loadError ? (
              <TableErrorRow colSpan={6} entity="usuários" onRetry={() => loadUsers(page)} />
            ) : users.length === 0 ? (
              <TableEmptyRow colSpan={6} entity="usuário" />
            ) : (
              users.map((user) => (
                <Tr key={user.id}>
                  <Td>
                    <Flex align="center">
                      {user.picture && (
                        <Image src={user.picture} alt={user.name} width="30px" height="30px" marginEnd={2} />
                      )}
                      {user.name}
                    </Flex>
                  </Td>
                  <Td>{renderStatus(user)}</Td>
                  <Td>
                    <Flex align="center">
                      {user.email}
                      {user.login_method == 'google' && (
                        <>
                          <Tooltip label="Acesso pelo Google">
                            <Image src="/img/google-icon.png" alt="Google" width="20px" height="20px" ml={2} />
                          </Tooltip>
                        </>
                      )}
                    </Flex>
                  </Td>
                  <Td>
                    {user.pending_approval && <Badge colorScheme="yellow">Pendente</Badge>}
                    {user.group ? (
                      <Badge mr={1}>{user.group.name}</Badge>
                    ) : (
                      <Text as="span" color="gray.500">
                        Grupo não definido
                      </Text>
                    )}
                  </Td>
                  <Td>
                    {user.sectors && user.sectors.length > 0 ? (
                      user.sectors.map((sector: ISector) => (
                        <Badge key={sector.id} mr={1}>
                          {sector.name}
                        </Badge>
                      ))
                    ) : (
                      <Text as="span" color="gray.500">
                        Setor não definido
                      </Text>
                    )}
                  </Td>
                  <Td>
                    {user.login_method == 'google' && (
                      <>
                        {user.pending_approval && permissions.addUser && (
                          <ActionButton action="approve" label="Aprovar acesso" onClick={() => handleOpenModal(user)} />
                        )}
                        {!user.pending_approval && permissions.editUser && (
                          <ActionButton action="edit" onClick={() => handleOpenModal(user)} />
                        )}
                      </>
                    )}
                    {user.login_method == 'email' && permissions.editUser && (
                      <ActionButton
                        action="edit"
                        onClick={() => {
                          setModalUser(user)
                          setIsEditModalOpen(true)
                        }}
                      />
                    )}
                    {!user.pending_approval && (
                      <>
                        {!user.restricted && permissions.restrictUser && (
                          <ActionButton
                            action="lock"
                            onClick={() => {
                              setModalUser(user)
                              setRestrict(true)
                              onOpen()
                            }}
                          />
                        )}
                        {user.restricted && permissions.addUser && (
                          <ActionButton
                            action="unlock"
                            onClick={() => {
                              setModalUser(user)
                              setRestrict(false)
                              onOpen()
                            }}
                          />
                        )}
                      </>
                    )}
                    {permissions.deleteUser && (
                      <ActionButton
                        action="delete"
                        onClick={() => {
                          setModalUser(user)
                          onOpenDeleteDialog()
                        }}
                      />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={setPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(1)
        }}
      />
      <UserSetRolesModal isOpen={isModalOpen} onClose={handleCloseModal} user={modalUser} />
      <UserCreateModal isOpen={isUserCreateModalOpen} onClose={handleCloseUserCreateModal} />
      <UserEditModal isOpen={isEditModalOpen} onClose={handleCloseEditModal} user={modalUser} />
      <ConfirmDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleToggleRestriction}
        action={restrict ? 'lock' : 'unlock'}
        title={restrict ? 'Restringir acesso' : 'Liberar acesso'}
        description={
          <>
            Tem certeza que deseja {restrict ? 'restringir' : 'liberar'} o acesso de <strong>{modalUser?.name}</strong>?
          </>
        }
        isLoading={isSubmitting}
      />
      <ConfirmDialog
        isOpen={isOpenDeleteDialog}
        onClose={onCloseDeleteDialog}
        onConfirm={handleDelete}
        action="delete"
        entity="usuário"
        name={modalUser?.name}
        isLoading={isSubmitting}
      />

      {permissions.addUser && <ActionFab label="Adicionar usuário" onClick={() => setIsUserCreateModalOpen(true)} />}
    </>
  )
}
